import { useEffect, useMemo, useState } from 'react';

const DEMO_VIDEO_SRC = '/demo.mp4';

const DEMOS = [
  {
    id: 'research-workspace',
    title: 'Research Workspace Overview',
    description: 'A guided walkthrough of the end-to-end AI research environment, from project setup to experiment review.',
    duration: '02:18',
    tags: ['Workspace', 'Research', 'Overview'],
    accent: 'var(--cyan)',
  },
  {
    id: 'data-science-flow',
    title: 'Data Science Workflow',
    description: 'See how CSV, Excel, document, image, audio, and video labs fit into one interactive browser-native pipeline.',
    duration: '01:54',
    tags: ['Data Science', 'EDA', 'Browser'],
    accent: 'var(--green)',
  },
  {
    id: 'model-building',
    title: 'Model Building Labs',
    description: 'Walk through classical ML and deep learning labs that explain training dynamics with visual feedback.',
    duration: '02:42',
    tags: ['ML', 'DL', 'Training'],
    accent: 'var(--violet)',
  },
  {
    id: 'nlp-and-cv',
    title: 'NLP + Vision Modules',
    description: 'Explore language and vision labs that make token flow, attention, filters, and segmentation easy to inspect.',
    duration: '02:06',
    tags: ['NLP', 'CV', 'Interactive'],
    accent: 'var(--orange)',
  },
  {
    id: 'agentic-and-quantum',
    title: 'Agentic + Quantum Tours',
    description: 'A quick product demo covering tool use, planning, memory systems, and quantum AI experimentation.',
    duration: '03:01',
    tags: ['Agents', 'Quantum', 'Systems'],
    accent: 'var(--gold)',
  },
  {
    id: 'edge-runtime',
    title: 'Edge AI In Browser',
    description: 'See how edge AI modules run locally in the browser with model demos and on-device inference experiences.',
    duration: '01:47',
    tags: ['Edge AI', 'On-device', 'Demo'],
    accent: 'var(--red)',
  },
];

const alpha = (color, opacity) => `color-mix(in srgb, ${color} ${opacity}%, transparent)`;

function DemoModal({ demo, onClose }) {
  if (!demo) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,5,8,.58)',
        backdropFilter: 'blur(7px)',
        WebkitBackdropFilter: 'blur(7px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 80,
        padding: '20px',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '80vw',
          height: '80vh',
          maxWidth: '1280px',
          maxHeight: '820px',
          minHeight: '320px',
          background: '#000',
          border: '1px solid var(--b1)',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,.55)',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            background: 'rgba(6,11,20,.8)',
            border: '1px solid var(--b1)',
            color: 'var(--t2)',
            borderRadius: 4,
            width: 32,
            height: 32,
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
          }}
          aria-label="Close demo"
        >
          x
        </button>
        <div
          style={{
            position: 'absolute',
            left: 16,
            top: 16,
            zIndex: 2,
            display: 'grid',
            gap: 4,
            maxWidth: 'min(480px, calc(100% - 72px))',
            padding: '10px 12px',
            borderRadius: 6,
            background: 'rgba(6,11,20,.78)',
            border: '1px solid var(--b1)',
          }}
        >
          <div style={{ fontSize: 9, color: demo.accent, letterSpacing: '.14em' }}>VIDEO DEMO</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 22, color: 'var(--t1)', lineHeight: 1.1 }}>
            {demo.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.6 }}>
            {demo.description}
          </div>
        </div>
        <video
          controls
          autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
        >
          <source src={DEMO_VIDEO_SRC} type="video/mp4" />
          Your browser cannot play this video.
        </video>
      </div>
    </div>
  );
}

export default function DemosHub() {
  const [query, setQuery] = useState('');
  const [activeDemo, setActiveDemo] = useState(null);

  useEffect(() => {
    if (!activeDemo) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setActiveDemo(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeDemo]);

  const demos = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return DEMOS;
    return DEMOS.filter((demo) =>
      [demo.title, demo.description, ...demo.tags].join(' ').toLowerCase().includes(normalized)
    );
  }, [query]);

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'grid',
        alignContent: 'start',
        gap: '14px',
        paddingRight: '2px',
      }}
    >
      <div
        style={{
          border: '1px solid var(--b1)',
          borderRadius: '6px',
          padding: '18px',
          background: 'linear-gradient(145deg, var(--surface), var(--deep))',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: '760px' }}>
            <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.16em', marginBottom: '8px' }}>PRODUCT DEMOS</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '30px', lineHeight: 1.05, marginBottom: '10px', color: 'var(--t1)' }}>
              How-To's
            </div>
            <div style={{ fontSize: '12px', color: 'var(--t2)', lineHeight: 1.75 }}>
Discover how the platform works through real-world demos and step-by-step tutorials across all workflows.            </div>
          </div>
          <div style={{ minWidth: '240px', display: 'grid', gap: '8px', flex: '1 1 260px' }}>
            
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search demos by title, tag, or capability"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid var(--b1)',
                background: 'var(--deep)',
                color: 'var(--t1)',
                fontSize: '11px',
                fontFamily: 'JetBrains Mono',
                outline: 'none',
              }}
            />
          </div>
        </div>
      </div>

      {demos.length === 0 ? (
        <div
          style={{
            border: '1px dashed var(--b1)',
            borderRadius: '6px',
            padding: '26px',
            textAlign: 'center',
            background: 'var(--deep)',
            color: 'var(--t2)',
          }}
        >
          No demos match this search.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
          {demos.map((demo) => (
            <button
              key={demo.id}
              onClick={() => setActiveDemo(demo)}
              style={{
                textAlign: 'left',
                border: `1px solid ${alpha(demo.accent, 42)}`,
                borderRadius: '6px',
                background: `linear-gradient(145deg, ${alpha(demo.accent, 12)}, var(--surface))`,
                overflow: 'hidden',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <div style={{ position: 'relative', background: '#000' }}>
                <video
                  muted
                  playsInline
                  preload="metadata"
                  style={{ width: '100%', aspectRatio: '16 / 9', display: 'block', objectFit: 'cover', opacity: 0.86 }}
                >
                  <source src={DEMO_VIDEO_SRC} type="video/mp4" />
                </video>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(180deg, transparent, ${alpha('var(--void)', 72)})`,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    padding: '14px',
                  }}
                >
                  <div
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '50%',
                      background: alpha(demo.accent, 22),
                      border: `1px solid ${alpha(demo.accent, 55)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: demo.accent,
                      fontSize: '18px',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    ▶
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--t1)', fontFamily: 'JetBrains Mono', letterSpacing: '.08em' }}>
                    {demo.duration}
                  </div>
                </div>
              </div>
              <div style={{ padding: '14px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {demo.tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        padding: '4px 7px',
                        borderRadius: '999px',
                        border: `1px solid ${alpha(demo.accent, 35)}`,
                        background: alpha(demo.accent, 10),
                        color: demo.accent,
                        fontSize: '9px',
                        fontFamily: 'JetBrains Mono',
                        letterSpacing: '.08em',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: '16px', color: 'var(--t1)', fontWeight: 700, lineHeight: 1.3, marginBottom: '8px' }}>
                  {demo.title}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--t2)', lineHeight: 1.7 }}>
                  {demo.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <DemoModal demo={activeDemo} onClose={() => setActiveDemo(null)} />
    </div>
  );
}
